import { memo, FC, useCallback } from "react";
import { View, Text } from "@src/components";
import {
  type Granularity,
  GRANULARITY_OPTIONS,
  convertGridStateGranularity,
  getSlotsPerDay,
} from "./granularity-utils";
import { useGranularity, type GridStore } from "./grid-store";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { useUnistyles } from "react-native-unistyles";

interface GranularitySelectorProps {
  store: GridStore;
}

// Static label — never re-renders
const GranularityLabel = memo(() => (
  <Text textStyle="sm/regular" color="textSecondary" style={{ flexShrink: 0 }}>
    Granularity
  </Text>
));

// Subscribes to granularity in the store — only this component re-renders on switch
const GranularityControl = memo<{ store: GridStore }>(({ store }) => {
  const granularity = useGranularity(store) as Granularity;
  const selectedOption = GRANULARITY_OPTIONS.find((opt) => opt.id === granularity)?.name ?? GRANULARITY_OPTIONS[0].name;
  const { theme } = useUnistyles();
  const handleSelectOption = useCallback(
    (optionName: string) => {
      const selected = GRANULARITY_OPTIONS.find((opt) => opt.name === optionName);
      if (selected) {
        const { cells, initialCells, granularity: oldGranularity } = store.getState();
        store.setState({
          granularity: selected.id,
          cells: convertGridStateGranularity(cells, oldGranularity as Granularity, selected.id),
          initialCells: convertGridStateGranularity(initialCells, oldGranularity as Granularity, selected.id),
          slotsPerDay: getSlotsPerDay(selected.id),
        });
      }
    },
    [store],
  );

  return (
    <SegmentedSelector
      width={160}
      optionsLabels={GRANULARITY_OPTIONS.map((opt) => ({ title: opt.name }))}
      selectedOption={selectedOption}
      onSelectOption={handleSelectOption}
      containerStyle={{
        height: 40,
        backgroundColor: theme.colors.surface1.background,
        borderColor: theme.colors.field.border_default,
      }}
      activeBoxStyle={{ backgroundColor: theme.colors.surface1.hover }}
      textColorActiveBox="white"
    />
  );
});

// Outer shell — store prop is stable, so this never re-renders after mount
export const GranularitySelector: FC<GranularitySelectorProps> = memo(({ store }) => (
  <View flexDirection="row" gap="md" alignItems="center" justifyContent="space-between">
    <GranularityLabel />
    <View style={{ flexShrink: 0 }}>
      <GranularityControl store={store} />
    </View>
  </View>
));
